import React, { useState, Suspense, lazy, useEffect } from 'react';
import Box from '@mui/material/Box';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import Typography from '@mui/material/Typography';
import CircularProgress from '@mui/material/CircularProgress';
import { useQuery } from '@tanstack/react-query';
import axios from 'axios';
import Filters from './components/Filters';
import { useDispatch } from 'react-redux';
import { loadData } from './modules/sessionsSlice';

const API_BASE = process.env.REACT_APP_API_BASE_URL;

// Lazy load components for performance
const GroupedData = lazy(() => import('./components/GroupedData'));
const StationsContainer = lazy(() => import('./containers/StationsContainer'));
const AppsContainer = lazy(() => import('./containers/AppsContainer'));

function TabPanel(props) {
    const { children, value, index, ...other } = props;
    return (
        <div
            role="tabpanel"
            hidden={value !== index}
            id={`sessions-tabpanel-${index}`}
            aria-labelledby={`sessions-tab-${index}`}
            {...other}
        >
            {value === index && (
                <Box sx={{ p: 2 }}>
                    {children}
                </Box>
            )}
        </div>
    );
}

const fetchSessionAnalytics = async (dateParams) => {
    const { data, status } = await axios.get(`${API_BASE}/analytics/`, { params: dateParams });
    if (status === 200) {
        return data.data;
    }
    return "No data found";
};

const defaultDateParams = {
    dateRange: 'month',
    startDate: new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString().slice(0, 10),
    endDate: new Date().toISOString().slice(0, 10),
    aggregator: 'singleAndMultiStation',
    platform: 'all',
};

const Sessions = () => {
    const [tab, setTab] = useState(0);
    const [dateParams, setDateParams] = useState(defaultDateParams);
    const dispatch = useDispatch();

    useEffect(() => {
        dispatch(loadData(dateParams));
    }, [dispatch, dateParams]);

    const {
        data: summaryData,
        isLoading,
        isError,
        error,
    } = useQuery({
        queryKey: ['session-analytics', dateParams],
        queryFn: () => fetchSessionAnalytics(dateParams),
        keepPreviousData: true,
    });

    const handleTabChange = (event, newValue) => {
        setTab(newValue);
    };

    const handleApplyFilters = (filters) => {
        // If not custom, set start/end to undefined so backend can infer
        setDateParams(prev => ({
            ...prev,
            ...filters,
            startDate: filters.dateRange === 'custom' ? filters.startDate : undefined,
            endDate: filters.dateRange === 'custom' ? filters.endDate : undefined,
        }));
    };

    return (
        <Box sx={{ width: '100%', p: 3 }}>
            <Filters
                initialValues={dateParams}
                onApply={handleApplyFilters}
                loading={isLoading}
            />
            <Suspense fallback={<CircularProgress />}>
                {isLoading ? (
                    <CircularProgress />
                ) : isError ? (
                    <Typography color="error">{error?.message || 'Failed to load analytics.'}</Typography>
                ) : summaryData === 'No data found' ? (
                    <Typography color="error">No data found</Typography>
                ) : (
                    <GroupedData data={summaryData} loading={false} />
                )}
            </Suspense>
            <Box sx={{ borderBottom: 1, borderColor: 'divider', mt: 3 }}>
                <Tabs value={tab} onChange={handleTabChange} aria-label="sessions tabs">
                    <Tab label="Stations" id="sessions-tab-0" aria-controls="sessions-tabpanel-0" />
                    <Tab label="Apps" id="sessions-tab-1" aria-controls="sessions-tabpanel-1" />
                </Tabs>
            </Box>
            <Suspense fallback={<CircularProgress />}>
                <TabPanel value={tab} index={0}>
                    <StationsContainer dateParams={dateParams} />
                </TabPanel>
                <TabPanel value={tab} index={1}>
                    <AppsContainer />
                </TabPanel>
            </Suspense>
        </Box>
    );
};

export default Sessions; 